/**
 * App.js — Root Application Component
 *
 * Route structure:
 *  /login           → LoginPage          (public — tenant user login)
 *  /privacy         → PrivacyPolicy      (public — no auth required)
 *  /terms           → TermsOfService     (public — no auth required)
 *  /                → ChatPage           (protected — requires tenant JWT)
 *  /inqlect-admin/login → AdminLoginPage     (public — ABC admin key login)
 *  /inqlect-admin/*     → AdminLayout        (protected — requires admin key)
 *    /inqlect-admin             → OverviewPage
 *    /inqlect-admin/tenants     → TenantsPage
 *    /inqlect-admin/channels    → ChannelsPage
 *    /inqlect-admin/licences    → LicencesPage
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth }           from './context/AuthContext';
import { ThemeProvider }                   from './context/ThemeContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import ErrorBoundary                       from './components/ErrorBoundary';

import LoginPage      from './pages/LoginPage';
import ChatPage       from './pages/ChatPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout    from './pages/admin/AdminLayout';
import OverviewPage   from './pages/admin/OverviewPage';
import TenantsPage    from './pages/admin/TenantsPage';
import ChannelsPage   from './pages/admin/ChannelsPage';
import LicencesPage   from './pages/admin/LicencesPage';
import UsersPage             from './pages/admin/UsersPage';
import AnalyticsPage         from './pages/admin/AnalyticsPage';
import LogViewerPage         from './pages/admin/LogViewerPage';
import AdminSupportPage      from './pages/admin/AdminSupportPage';
import AdminTicketDetailPage from './pages/admin/AdminTicketDetailPage';
import ManageUsersPage          from './pages/ManageUsersPage';
import ProfilePage              from './pages/ProfilePage';
import ForceChangePasswordPage  from './pages/ForceChangePasswordPage';
import UsagePage                from './pages/UsagePage';
import SupportPage              from './pages/SupportPage';
import TicketDetailPage         from './pages/TicketDetailPage';
import PrivacyPolicy            from './pages/PrivacyPolicy';
import TermsOfService           from './pages/TermsOfService';

import './index.css';
import './styles/admin.css';

// ─── Route Guards ─────────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Admin forced a password reset — block all pages until user changes it
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  return children;
}


function AdminProtectedRoute({ children }) {
  const { isAdminAuthed } = useAdminAuth();
  return isAdminAuthed ? children : <Navigate to="/inqlect-admin/login" replace />;
}

// ─── App Routes ───────────────────────────────────────────────────────────────

function AppRoutes() {
  const { isAuthenticated, initializing } = useAuth();
  const { isAdminAuthed }                 = useAdminAuth();

  // Hold all route decisions until sessionStorage restore completes.
  // Without this, routes evaluate with user=null and redirect to /login
  // before must_change_password (or any other flag) is available.
  if (initializing) return null;

  return (
    <Routes>
      {/* ── Tenant user routes ─────────────────────────────── */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      {/* Force-change password — accessible when logged in but must_change_password=true */}
      <Route
        path="/change-password"
        element={
          !isAuthenticated
            ? <Navigate to="/login" replace />
            : <ForceChangePasswordPage />
        }
      />
      <Route
        path="/"
        element={<ProtectedRoute><ChatPage /></ProtectedRoute>}
      />
      <Route
        path="/manage-users"
        element={<ProtectedRoute><ManageUsersPage /></ProtectedRoute>}
      />
      <Route
        path="/profile"
        element={<ProtectedRoute><ProfilePage /></ProtectedRoute>}
      />
      <Route
        path="/usage"
        element={<ProtectedRoute><UsagePage /></ProtectedRoute>}
      />
      <Route
        path="/support"
        element={<ProtectedRoute><SupportPage /></ProtectedRoute>}
      />
      <Route
        path="/support/:ref"
        element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>}
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* ── ABC Admin routes ───────────────────────────────── */}
      <Route
        path="/inqlect-admin/login"
        element={isAdminAuthed ? <Navigate to="/inqlect-admin" replace /> : <AdminLoginPage />}
      />
      <Route
        path="/inqlect-admin"
        element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}
      >
        <Route index          element={<OverviewPage />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="users"   element={<UsersPage />} />
        <Route path="channels"element={<ChannelsPage />} />
        <Route path="licences"   element={<LicencesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="logs"      element={<LogViewerPage />} />
        <Route path="support"   element={<AdminSupportPage />} />
        <Route path="support/:ref" element={<AdminTicketDetailPage />} />
      </Route>

      {/* ── Catch-all ──────────────────────────────────────── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ─── Root Export ──────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        {/* Top-level ErrorBoundary catches any render crash that escapes inner boundaries */}
        <ErrorBoundary>
          <AuthProvider>
            <AdminAuthProvider>
              {/* Per-route ErrorBoundary: a crash in one route doesn't blank the whole app */}
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
            </AdminAuthProvider>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
}
